import React, { memo, useCallback } from 'react';
import { useObservable } from 'react-use-observable';

import { App, Button, Collapse, Input, Modal, Spin, Typography } from 'antd';

import { Field, Form, Formik } from 'formik';
import type { FormikProps } from 'formik';
import { HOST_ORBITA } from 'settings';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import affiliationsService from 'v2/services/affiliations';
import { useUserStore } from 'v2/store/User';

import { Icon } from '@eduzz/ui-antd-theme';

import type { IRecruitment } from './interfaces';
import RecruitmentSchema from './validations';

interface IProps {
  open: boolean;
  closeModal: (...args: any[]) => any;
}

const InitialValues = {
  email: ''
};

const RecruitmentCommission: React.FC<IProps> = ({ open, closeModal }) => {
  const { message } = App.useApp();
  const [isLoading] = useObservable(() => affiliationsService.isLoadingRecruitment(), []);
  const [product] = useObservable(() => affiliationsService.getProductRecruitment(), []);

  const { currentUserId } = useUserStore(({ user }) => ({ currentUserId: user.id }));

  const onSubmit = useCallback(
    (values: IRecruitment, { setSubmitting, resetForm }: FormikProps<IRecruitment>) => {
      setSubmitting(true);

      affiliationsService.inviteAffiliationByEmail(product, values.email).subscribe(
        () => {
          message.success('Convite enviado com sucesso.');
          setSubmitting(false);
          resetForm();
        },
        err => {
          const errorMessage =
            typeof err === 'string'
              ? err
              : err instanceof Error
                ? err.message
                : 'Não foi possível enviar o convite';

          message.error(errorMessage);
          setSubmitting(false);
        }
      );
    },
    [message, product]
  );

  if (!open) {
    return null;
  }

  const recruitmentLink = `${HOST_ORBITA}/affiliate/invite/${product?.id}/${currentUserId}`;

  const copyRecruitmentLink = async () => {
    try {
      await navigator.clipboard.writeText(recruitmentLink);
      message.success('Link copiado para a área de transferência');
    } catch {
      message.error('Não foi possível copiar o link');
    }
  };

  return (
    <Modal
      title='Comissão por recrutamento'
      open={open}
      onCancel={() => closeModal()}
      confirmLoading={!!isLoading}
      footer={
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button type='text' onClick={() => closeModal()}>
            Fechar
          </Button>
        </div>
      }
    >
      <div className='w-full'>
        {(isLoading === null || isLoading) && <Spin className='flex justify-center' />}

        {!isLoading && !product && (
          <span className='block text-sm'>Algo deu errado, tente novamente mais tarde...</span>
        )}

        {!isLoading && product && (
          <>
            <div className='flex flex-col gap-4'>
              <Typography.Paragraph className='mb-0'>
                Este conteúdo paga comissão por Indicações de Afiliados:
                <br />
                {product.recruit_commission_type && product.recruit_commission_type === 'P' && (
                  <>
                    Comissão: <Typography.Text strong>{product.recruit_commission_value}%</Typography.Text>
                    {product.recruit_max_sales ? ` nas ${product.recruit_max_sales} primeiras vendas.` : null}
                  </>
                )}
                {product.recruit_commission_type && product.recruit_commission_type !== 'P' && (
                  <>
                    Comissão: <Typography.Text strong>R${product.recruit_commission_value}</Typography.Text>
                    {product.recruit_max_sales ? ` nas ${product.recruit_max_sales} primeiras vendas.` : null}
                  </>
                )}
              </Typography.Paragraph>

              <Collapse
                defaultActiveKey={['link']}
                items={[
                  {
                    key: 'link',
                    label: 'Convite por link',
                    children: (
                      <div className='flex flex-col gap-2'>
                        <Input
                          value={recruitmentLink}
                          readOnly
                          suffix={
                            <Button
                              type='text'
                              shape='circle'
                              aria-label='Copiar link'
                              icon={<Icon.Copy size={16} />}
                              onClick={copyRecruitmentLink}
                            />
                          }
                        />
                        <Typography.Text type='secondary' className='text-xs'>
                          Divulgue esse link para que se cadastrem automaticamente atrelados ao seu conteúdo.
                        </Typography.Text>
                      </div>
                    )
                  }
                ]}
              />

              <Collapse
                items={[
                  {
                    key: 'email',
                    label: 'Convidar por e-mail',
                    children: (
                      <div className='flex flex-col gap-2'>
                        <Formik initialValues={InitialValues} validationSchema={RecruitmentSchema} onSubmit={onSubmit}>
                          {({ isSubmitting, setFieldValue, values }) => (
                            <Form>
                              <Field
                                id='email'
                                name='email'
                                fullWidth
                                disabled={isSubmitting}
                                component={TextFormField}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                  setFieldValue('email', e.target.value)
                                }
                                suffix={
                                  <Button
                                    type='text'
                                    htmlType='submit'
                                    icon={<Icon.Send size={16} />}
                                    loading={isSubmitting}
                                    disabled={isSubmitting || !values.email}
                                  />
                                }
                              />
                            </Form>
                          )}
                        </Formik>
                        <Typography.Text type='secondary' className='text-xs'>
                          O afiliado precisa estar cadastrado na Eduzz.
                        </Typography.Text>
                      </div>
                    )
                  }
                ]}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default memo(RecruitmentCommission);
